import type { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';
import { Skeleton } from '@accure/ui';
import { ExternalLink } from 'lucide-react';

import { FullPageContent } from '@/components/layout/full-page-content';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { AddTaskModalProvider } from '@/project-management/modals/add-task';
import { AddWorkstreamModalProvider } from '@/project-management/modals/add-workstream';
import { DeleteWorkstreamModalProvider } from '@/project-management/modals/delete-workstream';
import { DetachTaskModalProvider } from '@/project-management/modals/detach-task';
import { EditWorkstreamModalProvider } from '@/project-management/modals/edit-workstream';
import { WorkstreamsQuery } from '@/project-management/queries/workstreams.query';
import { getTaskLink } from '@/project-management/utils/get-task-link';
import { siteLabel } from '@/utils/format/customer';

export type ProjectShellProps = {
  className?: string;
  testId?: string;
};

export function ProjectShell({
  children,
  className,
  testId,
}: PropsWithChildren<ProjectShellProps>) {
  const prefetchWorkstreams = WorkstreamsQuery.usePrefetch();
  const { asset } = useCurrentAsset();

  return (
    <DeleteWorkstreamModalProvider>
      <DetachTaskModalProvider>
        <EditWorkstreamModalProvider>
          <AddWorkstreamModalProvider>
            <AddTaskModalProvider>
              <FullPageContent
                scrollable
                className="bg-zinc-50"
                testId={testId}
              >
                <div className="mx-auto max-w-6xl space-y-6 p-6">
                  <div className="mb-6">
                    {asset ? (
                      <h1 className="text-3xl font-bold">
                        {siteLabel(asset.fullFrontendId)}
                      </h1>
                    ) : (
                      <Skeleton className="h-9 w-64" />
                    )}
                  </div>

                  <nav className="flex h-9 items-center justify-between rounded-lg bg-gray-100 text-gray-600">
                    <div className="flex items-center gap-1 p-1">
                      <NavLink
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        activeClassName="bg-white text-gray-900 shadow-sm hover:bg-white hover:text-gray-900"
                        to="./timeline"
                        onMouseOver={() =>
                          prefetchWorkstreams({ siteId: asset!.id })
                        }
                      >
                        Timeline
                      </NavLink>
                      <NavLink
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        activeClassName="bg-white text-gray-900 shadow-sm hover:bg-white hover:text-gray-900"
                        to="./workstreams"
                        onMouseOver={() =>
                          prefetchWorkstreams({ siteId: asset!.id })
                        }
                      >
                        Workstreams
                      </NavLink>
                    </div>
                    <div className="flex items-center gap-1 p-1">
                      <NavLink
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        activeClassName="bg-white text-gray-900 shadow-sm hover:bg-white hover:text-gray-900"
                        to={getTaskLink({})}
                      >
                        Tasks
                        <ExternalLink className="ml-1 size-3" />
                      </NavLink>
                    </div>
                  </nav>

                  <div className={className}>{children}</div>
                </div>
              </FullPageContent>
            </AddTaskModalProvider>
          </AddWorkstreamModalProvider>
        </EditWorkstreamModalProvider>
      </DetachTaskModalProvider>
    </DeleteWorkstreamModalProvider>
  );
}
